import { useEffect, useMemo, useRef, type ReactNode } from "react";
import type { ActionResult, JiaxingCase, KnowledgeCard, QuestionCard, ReflectionResult, TicketData } from "@/types/journey";
import { ACTION_SHORTCUTS, DEFAULT_DEMO_QUESTION, JOURNEY_STEPS, SHORTCUT_QUESTIONS, SENSITIVE_INPUT_REMINDER } from "@/lib/constants";
import { characterCount } from "@/lib/text";
import { ProgressRail } from "@/components/ProgressRail";
import { ChoiceCard } from "@/components/ChoiceCard";

function Eyebrow({ children }: { children: string }) {
  return <p className="eyebrow">{children}</p>;
}

function PageTitle({ eyebrow, title, subtitle }: { eyebrow?: string; title: ReactNode; subtitle?: string }) {
  return (
    <div className="page-title">
      {eyebrow ? <Eyebrow>{eyebrow}</Eyebrow> : null}
      <h1>{title}</h1>
      {subtitle ? <p className="page-subtitle">{subtitle}</p> : null}
    </div>
  );
}

export function LoadingScreen() {
  return (
    <main className="loading-screen">
      <div className="loading-ripple" aria-hidden="true"><span /><span /><span /></div>
      <div className="loading-boat" aria-hidden="true">
        <svg viewBox="0 0 280 120"><path d="M45 73c51 15 127 16 190-9l-16 30c-63 19-126 18-164 4z" /><path d="M57 67h170l-19 14c-54 12-104 12-145 3z" /><path d="M134 66V16l42 35" /><path d="M137 22l38 29h-38z" /></svg>
      </div>
      <Eyebrow>RED BOAT THREE QUESTIONS / 01</Eyebrow>
      <h1>红船三问</h1>
      <p>AI时代的青年答案</p>
      <div className="loading-caption"><span className="loading-dot" />正在驶向南湖……</div>
    </main>
  );
}

export function IntroPage({ isDemo, onStart }: { isDemo: boolean; onStart: () => void }) {
  return (
    <section className="intro-page" data-testid="intro">
      <div className="intro-copy">
        <Eyebrow>南湖 · 清晨 / 1921 — 2026</Eyebrow>
        <h1>有些问题，<br /><em>答案在出发之后。</em></h1>
        <div className="intro-story">
          <p>1921年，<br />一群年轻人在这里作出了自己的选择。</p>
          <p>一百多年以后，<br />很多问题已经改变。</p>
          <p>但选择，<br />依然每天发生。</p>
        </div>
        <div className="intro-question">
          <span>如果今天站在红船旁，</span>
          <strong>你最想问什么？</strong>
        </div>
        {isDemo ? <div className="demo-note"><span>DEMO</span> 已预填一条示例问题，适合录制两分钟演示。</div> : null}
        <button className="button button-primary button-large" type="button" onClick={onStart} data-testid="start-journey">
          {isDemo ? "进入示例路线" : "开始我的三问"}<span aria-hidden="true">↗</span>
        </button>
      </div>
      <div className="intro-aside" aria-hidden="true">
        <span className="aside-index">01</span>
        <span className="aside-rule" />
        <span className="aside-label">ONE QUESTION<br />THREE CHOICES<br />ONE NEXT STEP</span>
      </div>
    </section>
  );
}

interface QuestionInputProps {
  question: string;
  isDemo: boolean;
  onChange: (value: string) => void;
  onSubmit: () => void;
  busy?: boolean;
}

export function QuestionInputPage({ question, isDemo, onChange, onSubmit, busy }: QuestionInputProps) {
  const count = characterCount(question);
  const valid = count >= 5 && count <= 20;
  return (
    <section className="journey-page question-input-page" data-testid="question-input">
      <ProgressRail active={0} />
      <div className="split-heading">
        <PageTitle eyebrow="先从你开始 / QUESTION 00" title="先不谈大道理。" subtitle="写下一件最近真正让你犹豫、困惑或者想不明白的事。" />
        <div className="margin-note">不需要标准答案<br />只需要你的真实问题</div>
      </div>
      <div className="question-form-wrap">
        <label className="sr-only" htmlFor="question-input-field">你的问题</label>
        <textarea
          id="question-input-field"
          value={question}
          onChange={(event) => onChange(event.target.value)}
          placeholder={'例如：\n“我还该坚持吗？”'}
          maxLength={20}
          rows={4}
          data-testid="question-field"
        />
        <div className="field-meta"><span className={count > 20 ? "is-error" : ""}>{count} / 20（至少 5 字）</span><span>输入会留在这次体验里，不建立账号。</span></div>
        <p className="privacy-hint"><span aria-hidden="true">⌁</span> {SENSITIVE_INPUT_REMINDER}</p>
        <div className="shortcut-block">
          <p>或者，从一个常被问起的问题开始</p>
          <div className="shortcut-list">
            {SHORTCUT_QUESTIONS.map((item) => <button type="button" key={item} onClick={() => onChange(item)}>{item}</button>)}
          </div>
        </div>
        <button className="button button-primary button-large submit-question" type="button" onClick={onSubmit} disabled={!valid || busy}>
          {busy ? "正在听见你的问题…" : "把这个问题交给红船"}<span aria-hidden="true">↗</span>
        </button>
        {isDemo ? <button className="demo-fill" type="button" onClick={() => onChange(DEFAULT_DEMO_QUESTION)}>恢复示例问题</button> : null}
      </div>
    </section>
  );
}

export function AnalysisLoadingPage() {
  return (
    <section className="journey-page analysis-loading" data-testid="analysis-loading">
      <div className="analysis-orbit" aria-hidden="true"><span /><span /><span /></div>
      <Eyebrow>AI LISTENING / 不替你下结论</Eyebrow>
      <h1>先把你的问题，<br /><em>放在水面上。</em></h1>
      <p>正在寻找它真正关心的那一小块地方……</p>
      <div className="loading-lines"><span /><span /><span /></div>
    </section>
  );
}

interface BinaryQuestionProps {
  index: 1 | 2;
  question: QuestionCard;
  selected: "A" | "B" | null;
  pending?: boolean;
  onSelect: (value: "A" | "B") => void;
}

export function BinaryQuestionPage({ index, question, selected, pending, onSelect }: BinaryQuestionProps) {
  const isFirst = index === 1;
  return (
    <section className="journey-page question-page" data-testid={`question-${index === 1 ? "one" : "two"}`}>
      <ProgressRail active={index - 1} />
      <PageTitle eyebrow={`第 ${index} 问 / ${JOURNEY_STEPS[index - 1].label.toUpperCase()}`} title={isFirst ? "第一问 · 首创" : "第二问 · 奋斗"} subtitle={isFirst ? "当没有现成答案时，你会怎么选择？" : "如果已经出发，却迟迟看不到结果呢？"} />
      <div className="question-card-shell">
        <span className="question-card-index">0{index}</span>
        <p className="question-context">{question.context}</p>
        <div className="choices">
          <ChoiceCard label="A" selected={selected === "A"} disabled={Boolean(selected) || pending} onClick={() => onSelect("A")}>{question.optionA}</ChoiceCard>
          <ChoiceCard label="B" selected={selected === "B"} disabled={Boolean(selected) || pending} onClick={() => onSelect("B")}>{question.optionB}</ChoiceCard>
        </div>
        <div className={`choice-feedback ${selected ? "is-visible" : ""}`} aria-live="polite">
          <span className="feedback-ripple" aria-hidden="true">◌</span>
          <span>{pending ? "红船正在向前……" : "选择已留下。"}</span>
        </div>
      </div>
      <p className="question-footnote">没有正确或错误。你留下的是此刻的选择。</p>
    </section>
  );
}

interface ThirdQuestionProps {
  value: string;
  onChange: (value: string) => void;
  onSubmit: (value?: string) => void;
}

export function ThirdQuestionPage({ value, onChange, onSubmit }: ThirdQuestionProps) {
  const count = characterCount(value);
  return (
    <section className="journey-page question-page third-question-page" data-testid="question-three">
      <ProgressRail active={2} />
      <PageTitle eyebrow="第三问 / 奉献" title="第三问 · 奉献" subtitle="如果最后没有人知道这件事是你做的呢？" />
      <div className="third-answer-wrap">
        <label htmlFor="third-answer">我会……</label>
        <textarea id="third-answer" value={value} onChange={(event) => onChange(event.target.value)} maxLength={80} rows={3} placeholder="把你的回答留在这里" data-testid="third-answer-field" />
        <div className="field-meta"><span>{count} / 80</span><span>可以写一句，也可以先选择一个方向。</span></div>
        <div className="answer-shortcuts">
          {ACTION_SHORTCUTS.map((item) => <button type="button" key={item} onClick={() => onSubmit(item)}>{item}</button>)}
        </div>
        <button className="button button-primary" type="button" onClick={() => onSubmit()} disabled={!value.trim()} data-testid="submit-third">留下我的回答 <span aria-hidden="true">↗</span></button>
      </div>
      <p className="question-footnote">这不是表态题。你可以诚实地留下犹豫，或暂时不知道。</p>
    </section>
  );
}

export function RecapPage({ question, firstAnswer, secondAnswer, thirdAnswer, onContinue }: { question: string; firstAnswer: string; secondAnswer: string; thirdAnswer: string; onContinue: () => void }) {
  return (
    <section className="journey-page recap-page" data-testid="journey-recap">
      <ProgressRail active={3} />
      <PageTitle eyebrow="你的航迹 / RECAP" title="三问已答" subtitle="现在，再看看你最初的问题。" />
      <div className="recap-question"><span>你带来的问题</span><blockquote>“{question}”</blockquote></div>
      <div className="answer-trace">
        <div><span>01 / 首创</span><strong>{firstAnswer}</strong></div>
        <div><span>02 / 奋斗</span><strong>{secondAnswer}</strong></div>
        <div><span>03 / 奉献</span><strong>{thirdAnswer}</strong></div>
      </div>
      <button className="button button-primary button-large" type="button" onClick={onContinue}>看见自己的答案 <span aria-hidden="true">↗</span></button>
    </section>
  );
}

export function ReflectionPage({ question, reflection, source, loading, onContinue }: { question: string; reflection: ReflectionResult | null; source: string; loading: boolean; onContinue: () => void }) {
  return (
    <section className="journey-page reflection-page" data-testid="personal-reflection">
      <Eyebrow>AI MIRROR / 只做镜像，不替你判断</Eyebrow>
      <div className="reflection-origin"><span>你最开始问的是</span><blockquote>“{question}”</blockquote></div>
      <div className="reflection-layout">
        <div><PageTitle title={<>你真正问的，<br /><em>也许是另一件事。</em></>} /></div>
        <div className="reflection-note">
          {loading ? <><span className="mini-loader" />正在把三次选择放在一起……</> : <p>{reflection?.reflection}</p>}
          {!loading && source !== "api" ? <small>本次使用了本地预设线路，体验不因此中断。</small> : null}
        </div>
      </div>
      {!loading ? <button className="button button-primary" type="button" onClick={onContinue}>继续看见嘉兴 <span aria-hidden="true">↗</span></button> : null}
    </section>
  );
}

export function KnowledgePage({ cards, onContinue }: { cards: KnowledgeCard[]; onContinue: () => void }) {
  const themeLabels: Record<KnowledgeCard["theme"], string> = { first: "首创", struggle: "奋斗", dedication: "奉献" };
  return (
    <section className="journey-page knowledge-page" data-testid="knowledge">
      <Eyebrow>人工审核内容库 / KNOWLEDGE</Eyebrow>
      <PageTitle title={<>三问之后，<br /><em>留下一点更长的回声。</em></>} subtitle="这里的理论与事实内容，只从经过人工审核的资料库中出现。" />
      {cards.length ? <div className="knowledge-reading">{cards.map((card, index) => <article className="knowledge-card" key={card.id}><div className="knowledge-index">0{index + 1}</div><div className="knowledge-entry-heading"><span>{themeLabels[card.theme]}</span><h2>{card.title}</h2></div><div className="knowledge-entry-copy"><p>{card.content}</p>{card.display_quote ? <blockquote className="knowledge-quote">{card.display_quote}</blockquote> : null}{card.source_url ? <small>来源：<a href={card.source_url} target="_blank" rel="noreferrer">{card.source_name}</a></small> : <small>{card.source_name}</small>}</div></article>)}</div> : <div className="empty-content-card"><span className="empty-seal">待审</span><div><h2>理论卡片正在等待人工审核</h2><p>正式内容发布前，这里不会显示未经审核的阐释。三问的结构已经准备好，内容会在完成来源核验后出现。</p><small>审核完成后开放阅读</small></div></div>}
      <div className="knowledge-tracks"><span>首创</span><i /><span>奋斗</span><i /><span>奉献</span></div>
      <button className="button button-primary" type="button" onClick={onContinue}>看看嘉兴的答案 <span aria-hidden="true">↗</span></button>
    </section>
  );
}

export function CasePage({ item, onContinue }: { item: JiaxingCase | null; onContinue: () => void }) {
  return (
    <section className="journey-page case-page" data-testid="jiaxing-case">
      <Eyebrow>嘉兴实践 / JIAXING ANSWER</Eyebrow>
      <PageTitle title={<>嘉兴，也一直在<br /><em>回答属于这个时代的问题。</em></>} subtitle="你刚才回答的是自己的问题。" />
      {item ? <article className="case-card"><div className="case-card-top"><span>{item.region} · {item.category}</span><span>一个真实发生的答案</span></div><h2>{item.title}</h2><p>{item.content}</p><a href={item.source_url} target="_blank" rel="noreferrer">来源：{item.source_name} ↗</a></article> : <div className="empty-content-card case-empty"><span className="empty-seal">嘉兴</span><div><h2>嘉兴实践案例正在审核完善</h2><p>我们不会现场编写嘉兴实践，也不会把未核验的内容包装成事实。来源完成审核后，这里会出现一张真实案例卡片。</p><small>审核完成后开放阅读</small></div></div>}
      <button className="button button-primary" type="button" onClick={onContinue}>把答案带回明天 <span aria-hidden="true">↗</span></button>
    </section>
  );
}

export function ActionInputPage({ value, onChange, onSubmit }: { value: string; onChange: (value: string) => void; onSubmit: () => void }) {
  return (
    <section className="journey-page action-input-page" data-testid="action-input">
      <Eyebrow>你的下一步 / ACTION</Eyebrow>
      <PageTitle title={<>如果把刚才的答案，<br /><em>变成明天就能做的事——</em></>} subtitle="你会做什么？" />
      <div className="action-form-wrap">
        <label htmlFor="action-input">明天我会……</label>
        <textarea id="action-input" value={value} onChange={(event) => onChange(event.target.value)} maxLength={100} rows={3} placeholder="写一件具体的小事，不需要把它说得很大。" data-testid="action-field" />
        <div className="field-meta"><span>{characterCount(value)} / 100</span><span>越具体，越容易真正发生。</span></div>
        <div className="action-suggestions"><span>比如</span><button type="button" onClick={() => onChange("明天先花十分钟，把新方法试一遍")}>明天先花十分钟，把新方法试一遍</button><button type="button" onClick={() => onChange("明天和伙伴说清楚各自能承担的部分")}>明天和伙伴说清楚各自能承担的部分</button></div>
        <button className="button button-primary button-large" type="button" onClick={onSubmit} disabled={!value.trim()}>写下我的下一步 <span aria-hidden="true">↗</span></button>
      </div>
    </section>
  );
}

export function ActionRefinePage({ result, choice, onChoice, onContinue }: { result: ActionResult; choice: "refined" | "original"; onChoice: (value: "refined" | "original") => void; onContinue: () => void }) {
  return (
    <section className="journey-page action-refine-page" data-testid="action-refine">
      <Eyebrow>最后由你决定 / YOUR WORDS</Eyebrow>
      <PageTitle title={<>这句话，<br /><em>你想怎样带走？</em></>} subtitle="AI 只做了轻微整理，最后的决定属于你。" />
      <div className="action-options">
        <button type="button" className={`action-option ${choice === "refined" ? "is-selected" : ""}`} onClick={() => onChoice("refined")}><span className="option-radio" /> <small>AI 整理后</small><strong>{result.refined}</strong><em>更短 · 更具体</em></button>
        <button type="button" className={`action-option ${choice === "original" ? "is-selected" : ""}`} onClick={() => onChoice("original")}><span className="option-radio" /> <small>保留我的原话</small><strong>{result.original}</strong><em>你的原句 · 原样留下</em></button>
      </div>
      <button className="button button-primary button-large" type="button" onClick={onContinue}>生成我的船票 <span aria-hidden="true">↗</span></button>
    </section>
  );
}

export function TicketPage({ ticket, onGenerate, busy }: { ticket: TicketData; onGenerate: () => void; busy?: boolean }) {
  return (
    <section className={`journey-page ticket-page ${busy ? "is-generating" : ""}`} data-testid="ticket">
      <div className="ticket-heading"><div><Eyebrow>出发凭证 / PASS 01</Eyebrow><h1>红船青年船票</h1><p>不是抵达证明，是给下一步的一个约定。</p></div><span className="ticket-stamp" aria-hidden="true">出发</span></div>
      <div className="ticket-card">
        <span className="ticket-watermark" aria-hidden="true">南湖 / 01</span>
        <div className="ticket-card-header"><span>红船三问</span><span>RC / 2026</span></div>
        <div className="ticket-route"><div><small>出发地</small><strong>嘉兴 · 南湖</strong></div><span className="route-line"><i>→</i></span><div><small>下一站</small><strong>明天</strong></div></div>
        <div className="ticket-passenger"><small>乘客</small><strong>新时代青年</strong><span>生成日期 {ticket.issuedDate}</span></div>
        <div className="ticket-answer"><small>我的答案</small><p>{ticket.action}</p></div>
        <span className="ticket-seal-mark" aria-hidden="true">红船<small>出发</small></span>
        <div className="ticket-bottom"><span>{ticket.ticketCode}</span><span>{ticket.templateNo}</span><span>AI时代的青年答案</span></div>
      </div>
      <div key={busy ? "ticket-generated" : "ticket-waiting"} className="ticket-confirmation" aria-live="polite">{busy ? <><span className="ticket-confirmation-mark">✓</span>你的船票已生成</> : "水纹会记住你的下一步。"}</div>
      <p className="ticket-note">这张船票只写下你愿意带走的行动，不包含姓名或其他身份信息。</p>
      <button className="button button-primary button-large" type="button" onClick={onGenerate} disabled={busy} data-testid="generate-ticket">{busy ? "正在盖章……" : "生成我的船票"} <span aria-hidden="true">↗</span></button>
    </section>
  );
}

export function LightsPage({ count, action, onContinue }: { count: number; action: string; onContinue: () => void }) {
  const lowPower = typeof navigator !== "undefined" && (navigator.hardwareConcurrency ?? 8) <= 4;
  const pointCount = lowPower || (typeof window !== "undefined" && window.matchMedia("(prefers-reduced-motion: reduce)").matches) ? 24 : 42;
  const points = useMemo(() => Array.from({ length: pointCount }, (_, index) => {
    const row = Math.floor(index / 7);
    const col = index % 7;
    const isHull = row >= 3 && row <= 5 && col >= 1 && col <= 5;
    return { x: 18 + col * 10 + (row % 2) * 2, y: 34 + row * 7, active: isHull || index % 5 === 0 };
  }), [pointCount]);
  return (
    <section className="journey-page lights-page" data-testid="lights">
      <div className="lights-sky" aria-hidden="true"><div className="star-field">{points.map((point, index) => <i key={index} className={point.active ? "is-active" : ""} style={{ left: `${point.x}%`, top: `${point.y}%`, animationDelay: `${(index % 7) * 180}ms` }} />)}</div><div className="light-boat"><span /><span /><span /><span /><span /><span /><span /></div></div>
      <Eyebrow>南湖水面 / LIGHTS</Eyebrow>
      <h1>你的答案，<br /><em>已经留下一个光点。</em></h1>
      <p className="lights-copy">我们只保留一个抽象的行动关键词：<strong>{action}</strong>。不公开你的问题，也不公开你的原话。</p>
      <div className="lights-count"><strong>{count}</strong><span>位青年留下自己的答案</span></div>
      <button className="button button-primary" type="button" onClick={onContinue}>看见最后一页 <span aria-hidden="true">↗</span></button>
    </section>
  );
}

export function EndPage({ onRestart, onTicket, onAbout }: { onRestart: () => void; onTicket: () => void; onAbout: () => void }) {
  return (
    <section className="journey-page end-page" data-testid="end">
      <div className="end-emblem" aria-hidden="true"><span>红</span><small>船</small></div>
      <Eyebrow>红船三问 / THE END IS A BEGINNING</Eyebrow>
      <h1>一百多年前，<br />一条船从嘉兴出发。</h1>
      <p>今天，<br />每一个普通人的选择，<br />都可以成为新的出发。</p>
      <div className="end-actions"><button className="button button-primary button-large" type="button" onClick={onTicket}>查看我的船票 <span aria-hidden="true">↗</span></button><button className="button button-quiet" type="button" onClick={onRestart}>再问一次</button><button className="button button-quiet" type="button" onClick={onAbout}>关于 AI 创作</button></div>
    </section>
  );
}

export function AboutPage({ onBack }: { onBack: () => void }) {
  return (
    <section className="journey-page about-page" data-testid="about">
      <Eyebrow>创作说明 / ABOUT AI</Eyebrow>
      <PageTitle title={<>AI 在这里，<br /><em>但答案仍然属于你。</em></>} subtitle="本作品把生成式人工智能放在体验的背后，帮助你看见自己的选择。" />
      <div className="about-grid"><article><span>AI 参与</span><ul><li>问题理解</li><li>互动情境生成</li><li>文本总结</li><li>行动表达提炼</li><li>案例主题匹配</li></ul></article><article><span>人工负责</span><ul><li>理论审核</li><li>事实审核</li><li>嘉兴案例审核</li><li>视觉最终审核</li><li>内容发布</li></ul></article></div>
      <div className="about-statement">红船精神理论内容、党史事实及嘉兴实践案例由人工审核资料库提供，AI 不独立生成相关事实性内容。用户输入只用于完成本次体验，不建立账号，不公开完整表达。</div>
      <button className="button button-primary" type="button" onClick={onBack}>回到最后一页 <span aria-hidden="true">↗</span></button>
    </section>
  );
}

export function SharePage({ onStart }: { onStart: () => void }) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  useEffect(() => {
    let cancelled = false;
    void import("@/lib/qr").then(({ drawShareQr }) => {
      const shareUrl = import.meta.env.VITE_PUBLIC_URL || window.location.origin;
      if (!cancelled && canvasRef.current) return drawShareQr(canvasRef.current, shareUrl);
      return undefined;
    });
    return () => { cancelled = true; };
  }, []);
  return (
    <section className="journey-page share-page" data-testid="share">
      <div className="share-poster"><Eyebrow>SCAN TO BEGIN / 分享入口</Eyebrow><h1>把一个问题，<br /><em>带到红船旁。</em></h1><canvas ref={canvasRef} width={176} height={176} aria-label="扫码进入红船三问" /><small>扫码开始你的三问</small></div>
      <p>从一个真实的问题出发，通过首创、奋斗、奉献三问，留下属于自己的青年行动答案。</p>
      <button className="button button-primary button-large" type="button" onClick={onStart}>开始体验 <span aria-hidden="true">↗</span></button>
    </section>
  );
}
